import { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { LockKeyhole, ShieldCheck } from "lucide-react";
import { Button } from "../ui/button";
import { Skeleton } from "../ui/skeleton";
import { contextOf, isChoicePath, minutesLeft } from "../../lib/jamoraAdmin";
import useAdminFetch from "../../hooks/useAdminFetch";
import { Pill } from "./AdminBits";
import ContextSelector from "./ContextSelector";
import SessionGate from "./SessionGate";
import { AdminSessionProvider, useAdminSession } from "./SessionProvider";

function SessionInfo() {
  const { session, close } = useAdminSession();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => { const timer = setInterval(() => setNow(Date.now()), 30000); return () => clearInterval(timer); }, []);
  const left = minutesLeft(session.idle_expires_at, now);
  return (
    <div className="flex items-center gap-2 text-xs text-slate-500" data-testid="admin-session">
      <span className="hidden sm:inline" data-testid="admin-session-left">{left === null ? "Session ouverte" : `Session ouverte · se ferme après ${left} min sans activité`}</span>
      <Button variant="ghost" size="sm" className="h-8 text-slate-600" onClick={close} data-testid="admin-session-close"><LockKeyhole size={14} className="mr-1.5" />Fermer la session</Button>
    </div>
  );
}

function Banner({ context, pharmacy, choosing }) {
  const inPharmacy = context.kind === "pharmacie";
  const detail = pharmacy.data;
  return (
    <div className="rounded-lg border border-stone-200 bg-stone-50 px-4 py-2.5 flex flex-wrap items-center gap-x-3 gap-y-1.5" data-testid="admin-banner" role="status">
      <span className="text-sm text-slate-700">
        Vous consultez : <strong className="font-semibold text-slate-900" data-testid="admin-banner-context">
          {choosing ? "aucune pharmacie choisie" : !inPharmacy ? "Plateforme Jamora" : pharmacy.phase === "ready" ? detail.name : pharmacy.phase === "error" ? context.schema : "…"}
        </strong>
      </span>
      {inPharmacy && pharmacy.phase === "ready" && !detail.active && <Pill tone="red" testid="admin-banner-inactive">Désactivée</Pill>}
      {inPharmacy && pharmacy.phase === "ready" && detail.demo && <Pill tone="violet" testid="admin-banner-demo">Démonstration</Pill>}
      <Pill tone="slate" testid="admin-banner-readonly"><ShieldCheck size={12} className="mr-1" aria-hidden="true" />Lecture seule</Pill>
    </div>
  );
}

function Frame() {
  const { phase, session } = useAdminSession();
  const location = useLocation();
  const context = contextOf(location.pathname);
  const inPharmacy = context.kind === "pharmacie";
  const choosing = isChoicePath(location.pathname);
  // The pharmacy's file is read once here: the banner, the selector and the page share it.
  const pharmacy = useAdminFetch(inPharmacy ? `/pharmacies/${context.schema}` : null, undefined, { enabled: inPharmacy && session.active });

  if (phase === "loading") return <div className="space-y-3" data-testid="admin-boot"><Skeleton className="h-10 w-64" /><Skeleton className="h-24 w-full" /></div>;
  if (phase === "error") return <p className="text-sm text-slate-600" role="alert" data-testid="admin-boot-error">Impossible d'ouvrir l'administration pour le moment. Rechargez la page.</p>;
  if (!session.active) return <SessionGate />;

  const label = choosing ? "Choisir une pharmacie" : !inPharmacy ? "Plateforme Jamora" : pharmacy.phase === "ready" ? pharmacy.data.name : context.schema;
  return (
    <div className="space-y-4" data-testid="admin-app">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0 max-w-full sm:w-80"><ContextSelector context={context} label={label} choosing={choosing} /></div>
        <SessionInfo />
      </div>
      <Banner context={context} pharmacy={pharmacy} choosing={choosing} />
      <Outlet context={{ context, pharmacy }} />
    </div>
  );
}

export default function AdminShell() {
  return <AdminSessionProvider><Frame /></AdminSessionProvider>;
}
